// src/component/Home/OurClients/OurClients.jsx
import React, { useEffect, useRef } from "react";
import styled from "styled-components";

// ---- logos (keep your existing imports here)
import bosch from '../../../assets/images/LogoClient/bosch.png';
import estra from '../../../assets/images/LogoClient/estra.png';
import Kubota from '../../../assets/images/LogoClient/Kubota.png';
import hitachi from '../../../assets/images/LogoClient/tata-hitachi.png';
import Thermax from '../../../assets/images/LogoClient/Thermax.png';
import yazaki from '../../../assets/images/LogoClient/yazaki.png';
import shaktiman from '../../../assets/images/LogoClient/shaktiman.png';
import danieli from '../../../assets/images/LogoClient/danielicorus.png';
import ACG from '../../../assets/images/LogoClient/ACG.png';
import Bajaj from '../../../assets/images/LogoClient/Bajaj.png';
import Lm_wind_power from '../../../assets/images/LogoClient/Lm_wind_power.png';
import LandT from '../../../assets/images/LogoClient/L&T.png';

/* ========== STYLES ========== */

const Section = styled.section`
  padding: 64px 20px;
  /* Light, airy background (previous vibe) */
  background: radial-gradient(140% 120% at 0% 0%, #ffffff 0%, #f5f8fb 60%, #eef4f9 100%);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) { padding: 48px 15px; }
  @media (max-width: 576px) { padding: 38px 10px; }

  /* subtle ambient halo */
  &::before{
    content:"";
    position:absolute; inset:-12px;
    background: radial-gradient(70% 50% at 50% -10%,
      rgba(16,181,255,.20), rgba(16,181,255,0) 70%);
    filter: blur(30px);
    pointer-events:none;
  }

  /* reveal-on-scroll */
  [data-reveal]{ opacity:0; transform:translateY(16px); transition:opacity .42s ease, transform .42s ease; }
  [data-reveal].in{ opacity:1; transform:none; }
`;

const Title = styled.h4`
  font: 700 clamp(2rem, 3.5vw, 3rem)/1.1 "Poppins", system-ui;
  color:#06334b;
  margin:0 0 1.2rem;
  text-align:left;
  span{ color:#07b6f2; }
`;

const Frame = styled.div`
  margin-top: 8px;
  border-radius: 22px;
  padding: 22px 16px;
  background: radial-gradient(120% 120% at 0% 0%, #ffffff 0%, #f7fbff 100%);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.9),
    0 18px 46px rgba(12,36,54,.06);
`;

/* rotating neon palette */
const NEON = ["#6ecbff","#9f7bff","#ff7ebe","#ffd36e","#7bffcf","#6ea8ff"];

/* Neon card */
const Card = styled.div`
  --c: ${(p)=> NEON[p.$i % NEON.length]};
  --cA: ${(p)=> NEON[p.$i % NEON.length]}33;

  position:relative; height: 180px; border-radius: 28px; padding: 26px;
  display: grid; place-items: center; border: 1px solid rgba(7, 14, 30, .06);
  background:
    linear-gradient(180deg, #0d1732 0%, #0a1430 50%, #08122d 100%),
    repeating-linear-gradient(-45deg, rgba(255,255,255,.06) 0 2px, transparent 2px 8px);
  box-shadow: 0 10px 28px rgba(16,24,40,.18), 0 0 0 1px rgba(255,255,255,.03) inset;
  transform-style: preserve-3d; will-change: transform;
  transition: transform 180ms cubic-bezier(.2,.7,.2,1), box-shadow 180ms, border-color 180ms;

  /* top/bottom neon beams */
  &::before, &::after{
    content:""; position:absolute; left:50%; transform:translateX(-50%);
    width:72%; height:46px; filter: blur(12px); pointer-events:none; opacity:.85;
  }
  &::before{ top:-10px; background: radial-gradient(closest-side at 50% 100%, var(--c) 0%, rgba(255,255,255,.9) 20%, transparent 70%); }
  &::after { bottom:-10px; background: radial-gradient(closest-side at 50% 0%,   var(--c) 0%, rgba(255,255,255,.85) 20%, transparent 70%); }

  /* cursor hotspot glow */
  .hotspot{
    position:absolute; inset:-2px; border-radius: inherit; pointer-events:none;
    background: radial-gradient(240px 160px at var(--mx,50%) var(--my,50%), var(--cA), transparent 60%);
    opacity:0; transition:opacity .16s ease; filter:blur(16px);
  }

  &:hover{
    transform: perspective(900px) translateY(-8px) rotateX(3deg) scale(1.02);
    box-shadow: 0 16px 42px rgba(16,24,40,.28), 0 0 0 1px rgba(255,255,255,.05) inset, 0 0 48px 6px var(--cA);
    border-color: rgba(7,14,30,.10);
    .hotspot{ opacity:.95; }
  }

  @media (prefers-reduced-motion: reduce){
    transition:none !important; &:hover{ transform:none; } .hotspot{ display:none; }
  }
`;

/* Logo glow wrapper (color-matched) */
const LogoWrap = styled.div`
  position: relative; transform: translateZ(26px);

  &::before{
    content:""; position:absolute; inset:-18px -24px; border-radius: 24px;
    background:
      radial-gradient(120px 90px at 50% 60%, var(--cA), transparent 70%),
      radial-gradient(220px 160px at 50% 50%, rgba(255,255,255,.10), transparent 65%);
    filter: blur(14px);
    opacity: .55;
    z-index: 0;
    transition: opacity .18s ease, transform .18s ease;
  }
  ${Card}:hover &::before{
    opacity: .35;
    transform: scale(1.06);
  }
`;

/* translucent plate to keep logos readable on dark card */
const Plate = styled.div`
  position: relative; z-index: 1;
  background: rgba(255,255,255,.10);
  border: 1px solid rgba(255,255,255,.22);
  border-radius: 20px;
  padding: 18px 22px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.22), 0 10px 28px rgba(0,0,0,.4x5);
`;

const Logo = styled.img`
  width:auto; height:auto; max-width: 210px; max-height: 90px; object-fit:contain;
  filter: drop-shadow(0 0 6px rgba(255,255,255,.25)) drop-shadow(0 2px 10px rgba(0,0,0,.25));
  @media (max-width:576px){ max-width:170px; max-height:72px; }
`;

/* ========== HELPERS ========== */

function useReveal(sel="[data-reveal]"){
  useEffect(()=>{
    const els = document.querySelectorAll(sel); if(!els.length) return;
    const io = new IntersectionObserver((es)=>es.forEach(e=>{
      if(e.isIntersecting){ e.target.classList.add("in"); io.unobserve(e.target); }
    }),{ threshold:.12 });
    els.forEach((el,i)=>{ el.style.transitionDelay=`${Math.min(i*40,280)}ms`; io.observe(el); });
    return ()=>io.disconnect();
  },[sel]);
}

function useTiltGlow(){
  const prefersReduced = useRef(false);
  useEffect(()=>{
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    prefersReduced.current = mq.matches;
    const h = e => (prefersReduced.current = e.matches);
    mq.addEventListener?.("change",h); return ()=>mq.removeEventListener?.("change",h);
  },[]);
  const onMove = e => {
    if (prefersReduced.current) return;
    const el = e.currentTarget, r = el.getBoundingClientRect();
    const x = e.clientX - r.left, y = e.clientY - r.top;
    const rx = -((y - r.height/2)/(r.height/2))*8, ry = ((x - r.width/2)/(r.width/2))*8;
    el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-6px) scale(1.02)`;
    el.style.setProperty("--mx", `${(x/r.width)*100}%`);
    el.style.setProperty("--my", `${(y/r.height)*100}%`);
  };
  const onLeave = e => { e.currentTarget.style.transform = ""; };
  return { onMove, onLeave };
}

/* ========== COMPONENT ========== */

const OurClients = () => {
  useReveal();
  const tilt = useTiltGlow();

  const customers = [
    { name: 'Bajaj', logo: Bajaj },
    { name: 'Tata Hitachi', logo: hitachi },
    { name: 'Yazaki', logo: yazaki },
    { name: 'Thermax', logo: Thermax },
    { name: 'LM Wind Power', logo: Lm_wind_power },
    { name: 'Shaktiman', logo: shaktiman },
    { name: 'Estra', logo: estra },
    { name: 'ACG', logo: ACG },
    { name: 'Danieli Corus', logo: danieli },
    { name: 'Bosch', logo: bosch },
    { name: 'L&T Defence', logo: LandT },
    { name: 'Kubota', logo: Kubota },
  ];

  return (
    <Section aria-label="Our clients">
      <div className="container">
        <Title>Our Valuable <span>Clients</span></Title>

        <Frame className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3 g-md-4 justify-content-center">
          {customers.map((c, i) => (
            <div key={c.name} className="col" data-reveal>
              <Card
                $i={i}
                tabIndex={0}
                aria-label={c.name}
                onMouseMove={tilt.onMove}
                onMouseLeave={tilt.onLeave}
                onMouseEnter={tilt.onMove}
              >
                <div className="hotspot" />
                <LogoWrap>
                  <Plate>
                    <Logo
                      src={c.logo}
                      alt={`${c.name} logo`}
                      loading="lazy"
                      decoding="async"
                      width="210"
                      height="90"
                    />
                  </Plate>
                </LogoWrap>
              </Card>
            </div>
          ))}
        </Frame>
      </div>
    </Section>
  );
};

export default OurClients;
